import {Text} from "@/components/tailwind/text"
// import {Trans, useTranslation} from "react-i18next";
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useCallback, useEffect, useState} from "react";
import {useAuth} from "@/context/use-auth.tsx";
import {
    CurrentPointsDTO,
    fetchAssociatedServicesForMember,
    generateCompleteMemberName,
    getMemberData,
    getMemberFormOptions,
    cancelMembership,
    revokeMembershipCancellation,
    ResignationPointsTarget
} from "@/services/member-services.ts";
import {Member, MemberFormOptions, MemberService} from "@/models/member.ts";
import {
    getDonationsForMember,
    deleteOneTimeDonation,
    endAnnualDonation
} from "@/services/donation-services.ts";
import {AnnualDonation, MemberDonations, OneTimeDonation} from "@/models/donation.ts";
import OneTimeDonationDialog from "@/components/one-time-donation-dialog.tsx";
import AnnualDonationDialog from "@/components/annual-donation-dialog.tsx";
import {Dialog, DialogBody, DialogTitle} from "@/components/tailwind/dialog";
import {useToast} from "@/hooks/use-toast.ts";
import ConfirmationDialog from "@/components/confirmation-dialog/confirmation-dialog.tsx";
import {isDefined} from "@/helpers/sanitizer.ts";
import {Button} from "@/components/tailwind/button.tsx";
import {Divider} from "@/components/tailwind/divider.tsx";
import {Card} from "@/components/ui/card";
import {Avatar, AvatarFallback} from "@/components/ui/avatar";
import {
    Activity,
    AlertTriangle,
    Banknote,
    Briefcase,
    Building,
    Cake,
    CalendarDays,
    Car,
    Check,
    Clock,
    Coins,
    CreditCard,
    FileSignature,
    Gift,
    Hash,
    Info,
    Lock,
    Mail,
    MapPin,
    MessageSquare,
    Pencil,
    Plus,
    StopCircle,
    Trash2,
    User,
    Users,
    X,
    Zap
} from 'lucide-react';
import {Heading, Subheading} from "@/components/tailwind/heading.tsx";
import {Link} from "@/components/tailwind/link.tsx";
import {differenceInYears, format, parseISO} from "date-fns";
import MemberActionsButton from "@/components/member-actions-button.tsx";
import MemberEditNamesDialog from "@/components/member-edit-names-dialog.tsx";
import MemberEditCommentsDialog from "@/components/member-edit-comments-dialog.tsx";
import PhoneNumberDisplay from "@/components/phone-number-display.tsx";
import MemberEditContactDetailsDialog from "@/components/member-edit-contact-details-dialog.tsx";
import MemberEditMembershipDialog from "@/components/member-edit-membership-dialog.tsx";
import MemberEditBankingDetailsDialog from "@/components/member-edit-bankdetails-dialog.tsx";
import {Badge} from "@/components/tailwind/badge";
import MemberEditServicesDialog from "@/components/member-edit-services-dialog.tsx";
import MemberConsentSection from "@/components/member-consent-section.tsx";
import {useMembersTranslation, useCommonTranslation} from "@/i18n/hooks";
import {
    getCurrentMemberFeeAssignment,
    getMembershipFeeTypes,
    MemberFeeAssignment,
    MembershipFeeType
} from "@/services/membership-fee-services";
import log from '@/lib/logger'
import {MemberAuditLogSection} from "@/pages/members/details/member-audit-log-section";

// Allowed return routes - whitelist to prevent open redirect vulnerability
const VALID_RETURN_ROUTES: Record<string, string> = {
    'members': '/members',
    'drivers': '/reports/drivers',
    'bank-data-validation': '/reports/bank-data-validation',
} as const

export default function MemberDetailsPage() {
    const {t} = useMembersTranslation()
    const {t: tCommon} = useCommonTranslation()
    const {memberId} = useParams()
    const {token} = useAuth()
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()

    // One-time banner shown right after a member was created, so office staff can
    // read the freshly assigned membership number. Captured once from the router
    // navigation state; the state is then cleared so a page refresh does not re-show it.
    const [showCreationBanner] = useState<boolean>(
        () => (location.state as {justCreated?: boolean} | null)?.justCreated === true
    )

    useEffect(() => {
        if (showCreationBanner && location.state) {
            // Clear the one-time navigation state through the router (preserving React
            // Router's internal history bookkeeping) so a refresh does not re-show it.
            navigate(location.pathname + location.search, {replace: true})
        }
    }, [showCreationBanner, location.pathname, location.search, location.state, navigate])

    // Get return route from URL parameter
    // First check for explicit returnUrl (for preserving filter state)
    // Then fall back to 'from' parameter with whitelist validation
    const returnUrlParam = searchParams.get('returnUrl')
    const fromParam = searchParams.get('from') || 'members'
    const returnRoute = returnUrlParam 
        ? decodeURIComponent(returnUrlParam)
        : (VALID_RETURN_ROUTES[fromParam] || '/members')

    const [isEditNamesDialogOpen, setIsEditNamesDialogOpen] = useState(false);
    const [isEditCommentsDialogOpen, setIsEditCommentsDialogOpen] = useState(false);
    const [isEditContactDetailsDialogOpen, setIsEditContactDetailsDialogOpen] = useState(false);
    const [isEditMembershipDialogOpen, setIsEditMembershipDialogOpen] = useState(false);
    const [isEditBankingDetailsDialogOpen, setIsEditBankingDetailsDialogOpen] = useState(false);
    const [isEditServicesDialogOpen, setIsEditServicesDialogOpen] = useState(false);
    const [memberData, setMemberData] = useState<Member>()
    const [memberDataRetrievalError, setMemberDataRetrievalError] = useState<boolean>(false)
    const [memberNotFound, setMemberNotFound] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [memberFormOptions, setMemberFormOptions] = useState<MemberFormOptions>()
    const [memberServices, setMemberServices] = useState<MemberService[]>([]);
    const [isServicesLoading, setIsServicesLoading] = useState<boolean>(true);
    const [isServicesLoadError, setIsServicesLoadError] = useState<boolean>(false);
    const [currentFeeAssignment, setCurrentFeeAssignment] = useState<MemberFeeAssignment | null>(null);
    const [membershipFeeTypes, setMembershipFeeTypes] = useState<MembershipFeeType[]>([]);
    const [donations, setDonations] = useState<MemberDonations | null>(null);
    const [isDonationsLoading, setIsDonationsLoading] = useState<boolean>(true);

    // Donation dialog states
    const [isAddOneTimeDonationDialogOpen, setIsAddOneTimeDonationDialogOpen] = useState(false);
    const [isEditOneTimeDonationDialogOpen, setIsEditOneTimeDonationDialogOpen] = useState(false);
    const [editingOneTimeDonation, setEditingOneTimeDonation] = useState<OneTimeDonation | null>(null);
    const [isAddAnnualDonationDialogOpen, setIsAddAnnualDonationDialogOpen] = useState(false);
    const [isEditAnnualDonationDialogOpen, setIsEditAnnualDonationDialogOpen] = useState(false);
    const [editingAnnualDonation, setEditingAnnualDonation] = useState<AnnualDonation | null>(null);
    const [deletingOneTimeDonation, setDeletingOneTimeDonation] = useState<OneTimeDonation | null>(null);
    const [endingAnnualDonation, setEndingAnnualDonation] = useState<AnnualDonation | null>(null);
    const [isDeleteOneTimeConfirmOpen, setIsDeleteOneTimeConfirmOpen] = useState(false);
    const [isEndAnnualConfirmOpen, setIsEndAnnualConfirmOpen] = useState(false);
    const [isAnnualDonationHistoryOpen, setIsAnnualDonationHistoryOpen] = useState(false);
    const [auditLogRefreshTrigger, setAuditLogRefreshTrigger] = useState(0);
    const {toast} = useToast();

    const triggerAuditLogRefresh = useCallback(() => {
        setAuditLogRefreshTrigger(prev => prev + 1);
    }, []);

    const handleCancelMembership = (
        cancellationDate: string,
        resignationReasonId: string,
        pointsTarget?: ResignationPointsTarget,
        pointsTargetMemberId?: string
    ) => {
        if (!memberId) return;

        cancelMembership(token, memberId, {cancellationDate, resignationReasonId, pointsTarget, pointsTargetMemberId})
            .then(() => {
                toast({
                    title: t("memberDetails.toast.cancelMembershipSuccess.title"),
                    description: t("memberDetails.toast.cancelMembershipSuccess.description"),
                });
                navigate(returnRoute);
            })
            .catch((errorDetails) => {
                log.warn("Unable to cancel membership. Error details: ", errorDetails);
                toast({
                    title: tCommon("serverError.unknown"),
                    description: tCommon("serverError.unknown-with-details", {errorDetails: errorDetails.message || errorDetails}),
                    variant: "destructive",
                });
            });
    };

    const handleRevokeCancellation = () => {
        if (!memberId) return;

        revokeMembershipCancellation(token, memberId)
            .then(() => {
                toast({
                    title: t("memberDetails.toast.revokeCancellationSuccess.title"),
                    description: t("memberDetails.toast.revokeCancellationSuccess.description"),
                });
                loadAllData();
                triggerAuditLogRefresh();
            })
            .catch((errorDetails) => {
                log.warn("Unable to revoke membership cancellation. Error details: ", errorDetails);
                toast({
                    title: tCommon("serverError.unknown"),
                    description: tCommon("serverError.unknown-with-details", {errorDetails: errorDetails.message || errorDetails}),
                    variant: "destructive",
                });
            });
    };

    const handleAddPoints = (pointsAdded: CurrentPointsDTO) => {
        setMemberData(prevMemberData => {
            if (!prevMemberData) return prevMemberData;

            return {
                ...prevMemberData,
                memberPoints: {
                    ...prevMemberData.memberPoints,
                    points: pointsAdded.points,
                    lastUpdated: pointsAdded.statusFrom
                }
            };
        });
    };

    const handleDonatePoints = (newMemberBalance: number) => {
        setMemberData(prevMemberData => {
            if (!prevMemberData) return prevMemberData;

            return {
                ...prevMemberData,
                memberPoints: {
                    ...prevMemberData.memberPoints,
                    points: newMemberBalance,
                    lastUpdated: new Date().toISOString()
                }
            };
        });
    };

    // Donation handlers
    const handleAddOneTimeDonationSuccess = (donation: OneTimeDonation) => {
        setDonations(prev => {
            if (!prev) return {oneTimeDonations: [donation], annualDonations: [], nextBillingDate: null};
            return {
                ...prev,
                oneTimeDonations: [donation, ...prev.oneTimeDonations]
            };
        });
        toast({
            title: t("memberDetails.sections.donations.toast.addOneTimeSuccess.title"),
            description: t("memberDetails.sections.donations.toast.addOneTimeSuccess.description"),
        });
    };

    const handleEditOneTimeDonation = (donation: OneTimeDonation) => {
        setEditingOneTimeDonation(donation);
        setIsEditOneTimeDonationDialogOpen(true);
    };

    const handleEditOneTimeDonationSuccess = (_donation: OneTimeDonation) => {
        // Reload donations to get updated data
        loadDonations();
        toast({
            title: t("memberDetails.sections.donations.toast.editOneTimeSuccess.title"),
            description: t("memberDetails.sections.donations.toast.editOneTimeSuccess.description"),
        });
    };

    const handleDeleteOneTimeDonation = (donation: OneTimeDonation) => {
        setDeletingOneTimeDonation(donation);
        setIsDeleteOneTimeConfirmOpen(true);
    };

    const confirmDeleteOneTimeDonation = async () => {
        if (!deletingOneTimeDonation || !memberId) return;
        try {
            await deleteOneTimeDonation(token, memberId, deletingOneTimeDonation.id);
            setDonations(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    oneTimeDonations: prev.oneTimeDonations.filter(d => d.id !== deletingOneTimeDonation.id)
                };
            });
            toast({
                title: t("memberDetails.sections.donations.toast.deleteOneTimeSuccess.title"),
                description: t("memberDetails.sections.donations.toast.deleteOneTimeSuccess.description"),
            });
        } catch (_error) {
            toast({
                title: t("memberDetails.sections.donations.toast.error.title"),
                description: t("memberDetails.sections.donations.toast.error.description"),
                variant: "destructive"
            });
        } finally {
            setDeletingOneTimeDonation(null);
            setIsDeleteOneTimeConfirmOpen(false);
        }
    };

    const handleAddAnnualDonationSuccess = (donation: AnnualDonation) => {
        setDonations(prev => {
            if (!prev) return {oneTimeDonations: [], annualDonations: [donation], nextBillingDate: null};
            return {
                ...prev,
                annualDonations: [donation, ...prev.annualDonations]
            };
        });
        toast({
            title: t("memberDetails.sections.donations.toast.addAnnualSuccess.title"),
            description: t("memberDetails.sections.donations.toast.addAnnualSuccess.description"),
        });
    };

    const handleEditAnnualDonation = (donation: AnnualDonation) => {
        setEditingAnnualDonation(donation);
        setIsEditAnnualDonationDialogOpen(true);
    };

    const handleEditAnnualDonationSuccess = (_donation: AnnualDonation) => {
        // Reload donations to get updated list (old donation closed, new one created)
        loadDonations();
        toast({
            title: t("memberDetails.sections.donations.toast.editAnnualSuccess.title"),
            description: t("memberDetails.sections.donations.toast.editAnnualSuccess.description"),
        });
    };

    const handleEndAnnualDonation = (donation: AnnualDonation) => {
        setEndingAnnualDonation(donation);
        setIsEndAnnualConfirmOpen(true);
    };

    const confirmEndAnnualDonation = async () => {
        if (!endingAnnualDonation || !memberId) return;
        try {
            await endAnnualDonation(token, memberId, endingAnnualDonation.id);
            // Reload donations to get updated status
            loadDonations();
            toast({
                title: t("memberDetails.sections.donations.toast.endAnnualSuccess.title"),
                description: t("memberDetails.sections.donations.toast.endAnnualSuccess.description"),
            });
        } catch (_error) {
            toast({
                title: t("memberDetails.sections.donations.toast.error.title"),
                description: t("memberDetails.sections.donations.toast.error.description"),
                variant: "destructive"
            });
        } finally {
            setEndingAnnualDonation(null);
            setIsEndAnnualConfirmOpen(false);
        }
    };

    async function loadDonations() {
        try {
            const donationsResult = await getDonationsForMember(token, memberId!);
            if (donationsResult) {
                setDonations(donationsResult);
            }
        } catch (error) {
            log.error("Error loading donations:", error);
        }
    }

    async function loadAllData() {
        setIsLoading(true);
        setIsServicesLoading(true);
        setIsDonationsLoading(true);

        try {
            const memberDataPromise = getMemberData(token, memberId);
            const servicesPromise = fetchAssociatedServicesForMember(token, memberId);
            const currentFeePromise = getCurrentMemberFeeAssignment(token, memberId!);
            const feeTypesPromise = getMembershipFeeTypes(token);
            const donationsPromise = getDonationsForMember(token, memberId!);

            const [memberDataResult, servicesResult, currentFeeResult, feeTypesResult, donationsResult] = await Promise.allSettled([
                memberDataPromise,
                servicesPromise,
                currentFeePromise,
                feeTypesPromise,
                donationsPromise
            ]);

            if (memberDataResult.status === 'fulfilled' && memberDataResult.value) {
                setMemberData(memberDataResult.value);
            } else {
                const reason = memberDataResult.status === 'rejected' ? memberDataResult.reason : null;
                if (reason?.status === 404) {
                    setMemberNotFound(true);
                } else {
                    setMemberDataRetrievalError(true);
                }
                log.warn("Error loading member data:", reason || "No data");
            }

            if (servicesResult.status === 'fulfilled' && servicesResult.value) {
                setMemberServices(servicesResult.value);
            } else {
                setIsServicesLoadError(true);
                log.warn("Error loading associated services:",
                    servicesResult.status === 'rejected' ? servicesResult.reason : "No data");
            }

            if (currentFeeResult.status === 'fulfilled') {
                setCurrentFeeAssignment(currentFeeResult.value);
            } else if (currentFeeResult.status === 'rejected') {
                log.warn("Error loading current fee assignment:", currentFeeResult.reason);
            }

            if (feeTypesResult.status === 'fulfilled' && feeTypesResult.value) {
                setMembershipFeeTypes(feeTypesResult.value);
            }

            if (donationsResult.status === 'fulfilled' && donationsResult.value) {
                setDonations(donationsResult.value);
            } else if (donationsResult.status === 'rejected') {
                log.warn("Error loading donations:", donationsResult.reason);
            }
        } catch (error) {
            log.error("Error fetching member & associated service data:", error);
            setMemberDataRetrievalError(true);
            setIsServicesLoadError(true);
        } finally {
            setIsLoading(false);
            setIsServicesLoading(false);
            setIsDonationsLoading(false);
        }
    }

    async function loadFormOptions() {
        try {
            const options = await getMemberFormOptions(token!)
            if (options !== undefined) {
                setMemberFormOptions(options)
            }
        } catch (error) {
            log.warn("Error loading form options:", error)
        }
    }

    useEffect(() => {
        function initialLoad() {
            setIsLoading(true)

            Promise.all([loadAllData(), loadFormOptions()])
                .finally(() => {
                    setIsLoading(false)
                    setIsServicesLoading(false)
                    setIsDonationsLoading(false)
                });
        }

        initialLoad();
    }, [token, memberId]);

    if (isLoading || isServicesLoading || isDonationsLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Text className="text-gray-500">{tCommon("loading")}</Text>
            </div>
        )
    }

    if (memberNotFound) {
        return (
            <div className="flex flex-col justify-center items-center h-64 space-y-4">
                <Text className="text-gray-500">{t("memberDetails.memberExited")}</Text>
                <div className="flex gap-3">
                    <Button
                        onClick={() => navigate(`/members/${memberId}/retained-data`)}
                        data-testid="member-exited-retained-data-link"
                    >
                        {t("memberDetails.retainedDataLink")}
                    </Button>
                    <Button outline onClick={() => navigate(returnRoute)}>
                        {t("memberDetails.backToList")}
                    </Button>
                </div>
            </div>
        )
    }

    if (memberDataRetrievalError || isServicesLoadError) {
        return (
            <div className="flex justify-center items-center h-64">
                <Text className="text-red-500">{tCommon("loading-issue")}</Text>
            </div>
        )
    }

    function formatIBAN(iban: string | undefined) {
        if (isDefined(iban)) {
            const sanitizedIBAN = iban!.replace(/[^A-Za-z0-9*]/g, "");
            return sanitizedIBAN.replace(/(.{4})/g, "$1 ").trim();
        } else {
            return iban
        }
    }

    function accountOwner(givenAccountOwner: string | undefined): string {
        if (givenAccountOwner !== null) {
            return givenAccountOwner!
        }

        return memberData?.firstName + " " + memberData?.lastName;
    }

    if (memberData) {
        let companyDisplayName: string = ''
        if (memberData.companyName) {
            companyDisplayName = t("memberList.companyPrefix") + ` ${memberData.companyName}`
        }
        const memberDisplayName = generateCompleteMemberName(memberData)


        let memberAddress = ''
        if (memberData.address) {
            memberAddress = `${memberData.address.street} ${memberData.address.houseNumber}, ` +
                `${memberData.address.postcode} ${memberData.address.city}`
        }

        function formatDate(dateString: string | any) {
            if (!dateString) return '';
            try {
                // Handle both string dates and potential array/object formats from backend
                const dateStr = typeof dateString === 'string' 
                    ? dateString 
                    : Array.isArray(dateString) 
                        ? `${dateString[0]}-${String(dateString[1]).padStart(2, '0')}-${String(dateString[2]).padStart(2, '0')}`
                        : '';
                if (!dateStr) return '';
                return format(parseISO(dateStr), 'dd.MM.yyyy');
            } catch (error) {
                log.error('Date formatting error:', error, 'for value:', dateString);
                return '';
            }
        }

        function formatDateTime(dateTimeString: string | any) {
            if (!dateTimeString) return '';
            try {
                const dateStr = typeof dateTimeString === 'string' ? dateTimeString : '';
                if (!dateStr) return '';
                return format(parseISO(dateStr), 'dd.MM.yyyy HH:mm');
            } catch (error) {
                log.error('DateTime formatting error:', error, 'for value:', dateTimeString);
                return '';
            }
        }

        function calculateAge(birthDate: string | null | undefined) {
            if (!birthDate) return null;
            try {
                const date = parseISO(birthDate);
                return differenceInYears(new Date(), date);
            } catch (error) {
                log.error('Age calculation error:', error, 'for value:', birthDate);
                return null;
            }
        }

        const age = calculateAge(memberData.dateOfBirth)

        return (

            <>
                <div className="container mx-auto py-6 max-w-4xl space-y-6">
                    {showCreationBanner && (
                        <div
                            className="w-full rounded-md border border-green-300 bg-green-50 p-6 text-center dark:border-green-700 dark:bg-green-950"
                            role="status"
                            data-testid="member-created-banner"
                        >
                            <Text className="text-green-800 dark:text-green-200">
                                {t("memberDetails.creationBanner.title")}
                            </Text>
                            <div className="mt-2 text-5xl font-bold text-green-700 dark:text-green-300">
                                {memberData.memberNo}
                            </div>
                        </div>
                    )}
                    <div className={"w-full text-right"}>
                        <MemberActionsButton onAddPoints={handleAddPoints}
                                             onDonatePoints={handleDonatePoints}
                                             onCancelMembership={handleCancelMembership}
                                             onRevokeCancellation={handleRevokeCancellation}
                                             memberData={memberData}

                        />

                    </div>
                    <Card className="w-full">
                        <div className="p-6">
                            <div className="relative">
                                <div className="flex flex-col md:flex-row md:items-center gap-6">
                                    <Avatar className="h-24 w-24">
                                        <AvatarFallback>
                                            <User size={48}/>
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="space-y-2">
                                        <div>
                                            <Heading>{memberDisplayName}</Heading>
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">

                                            <div className="flex items-center gap-1">
                                                <Cake className="h-4 w-4"/>
                                                {memberData.dateOfBirth ?
                                                    <Text>
                                                        {formatDate(memberData.dateOfBirth)}
                                                    </Text> :
                                                    <Text
                                                        className={"italic"}>{tCommon("unknown-detail")}</Text>
                                                }
                                                {age && (
                                                    <>
                                                        <span className="text-gray-300 dark:text-gray-600 mx-1">•</span>
                                                        <Text>{age} {t("memberDetails.sections.main.ageQuantifier")}
                                                        </Text>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsEditNamesDialogOpen(true)}
                                    className="absolute top-0 right-0 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                                    aria-label={t("memberDetails.buttons.edit.main.ariaLabel")}
                                    data-testid="edit-member-names-button"
                                >
                                    <Pencil className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
                                </button>
                            </div>
                        </div>
                    </Card>

                    <Card>
                        <div className="p-6">
                            <div className="relative mb-6">
                                <div className="text-2xl font-semibold leading-none tracking-tight" role="heading">
                                    {t("memberDetails.sections.contacts.heading")}
                                </div>
                                <button
                                    onClick={() => setIsEditContactDetailsDialogOpen(true)}
                                    className="absolute top-0 right-0 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                                    aria-label={t("memberDetails.buttons.edit.contactDetails.ariaLabel")}
                                    data-testid="edit-member-contact-button"
                                >
                                    <Pencil className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
                                </button>
                            </div>

                            <div className="space-y-6">
                                <div className="flex items-center gap-3">
                                    <Mail className="h-4 w-4 text-gray-500"/>
                                    {memberData.email ?
                                        <Link type={"email"} href={`mailto:${memberData.email}`}>
                                            <Text>{memberData.email}</Text>
                                        </Link> :
                                        <Text className={"italic"}>{tCommon("unknown-detail")}</Text>
                                    }
                                </div>
                                {memberData.phoneNumbers && memberData.phoneNumbers.length > 0 ?
                                    memberData.phoneNumbers.map((phoneNumber) => (
                                        <PhoneNumberDisplay
                                            key={phoneNumber.id}
                                            id={phoneNumber.id}
                                            type={phoneNumber.type}
                                            number={phoneNumber.number}
                                        />
                                    )) : (
                                        <PhoneNumberDisplay type="UNKNOWN"/>
                                    )
                                }
                                <div className="flex items-center gap-3">
                                    <MapPin className="h-4 w-4 text-gray-500"/>
                                    {memberAddress ?
                                        <Text>{memberAddress}</Text> :
                                        <Text className={"italic"}>{tCommon("unknown-detail")}</Text>
                                    }
                                </div>
                                {memberData.address?.districtName && (
                                    <div className="flex items-center gap-3">
                                        <MapPin className="h-4 w-4 text-gray-500"/>
                                        <Text>{t("memberDetails.sections.contacts.district")}: {memberData.address.districtName}</Text>
                                    </div>
                                )}
                                {companyDisplayName && (
                                    <div className="flex items-center gap-3">
                                        <Building className="h-4 w-4 text-gray-500"/>
                                        <Text>{companyDisplayName}</Text>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>

                    <Card>
                        <div className="p-6">
                            <div className="relative mb-6">
                                <div className="text-2xl font-semibold leading-none tracking-tight" role="heading">
                                    {t("memberDetails.sections.membership.heading")}
                                </div>
                                <button
                                    onClick={() => setIsEditMembershipDialogOpen(true)}
                                    className="absolute top-0 right-0 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                                    aria-label={t("memberDetails.buttons.edit.membership.ariaLabel")}
                                    data-testid="edit-member-membership-button"
                                >
                                    <Pencil className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
                                </button>
                            </div>

                            {memberData.membershipEndDate && (
                                <div
                                    className="mb-6 flex items-start gap-3 rounded border-l-4 border-amber-400 dark:border-amber-500 bg-amber-50 dark:bg-amber-950 p-4"
                                    data-testid="member-resignation-notice"
                                >
                                    <AlertTriangle className="h-5 w-5 shrink-0 text-amber-500 dark:text-amber-400"/>
                                    <div>
                                        <Text className="font-semibold text-amber-800 dark:text-amber-200">
                                            {t("memberDetails.sections.membership.membershipEnd")} {formatDate(memberData.membershipEndDate)}
                                        </Text>
                                        {memberData.resignationReason && (
                                            <Text className="text-amber-700 dark:text-amber-300">
                                                {t("memberDetails.sections.membership.resignationReason")}: {memberData.resignationReason.name}
                                            </Text>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="flex items-center gap-3">
                                    <Hash className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
                                    <Text>{t("memberDetails.sections.membership.memberNo")}:&nbsp;
                                        {memberData.memberNo}
                                    </Text>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Users className="h-4 w-4 text-gray-500"/>
                                    <Text>{t("memberDetails.sections.membership.memberSince")}: {
                                        memberData.membershipStartDate ?
                                            formatDate(memberData.membershipStartDate) :
                                            <i>{tCommon("unknown-detail")}</i>
                                    }</Text>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Activity className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
                                    <Text>{t("memberDetails.sections.membership.status")}:&nbsp;
                                        {memberData.memberStatus === 'ACTIVE_MEMBER' ? 'Aktiv' : 'Passiv'}
                                    </Text>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Coins className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
                                    <Text>{t("memberDetails.sections.membership.points")}:&nbsp;
                                        {memberData.memberPoints?.points ?? '0'}
                                        <span className="mx-2 text-gray-300 dark:text-gray-600">·</span>
                                        <Link
                                            href={`/members/${memberId}/points-statement?from=member`}
                                            data-testid="member-points-statement-link"
                                            className="text-blue-600 hover:underline dark:text-blue-400"
                                        >
                                            {t("memberDetails.sections.membership.pointsStatementLink")}
                                        </Link>
                                    </Text>
                                </div>
                                
                                {/* Membership Fee Type */}
                                <div className="flex items-center gap-3">
                                    <CreditCard className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
                                    <Text>
                                        {t("memberDetails.sections.membership.feeType")}:&nbsp;
                                        {currentFeeAssignment ? (
                                            <span>
                                                {currentFeeAssignment.feeTypeName}
                                                {currentFeeAssignment.feeTypeAnnualAmount !== undefined && (
                                                    <span className="text-gray-600 dark:text-gray-400">
                                                        {' '}({currentFeeAssignment.feeTypeAnnualAmount === 0 
                                                            ? 'beitragsfrei' 
                                                            : `${currentFeeAssignment.feeTypeAnnualAmount.toFixed(2)} €/Jahr`})
                                                    </span>
                                                )}
                                            </span>
                                        ) : (
                                            <span className="italic text-gray-500">
                                                {t("memberDetails.sections.membership.noFeeAssigned")}
                                            </span>
                                        )}
                                    </Text>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {memberData.memberStatus === 'ACTIVE_MEMBER' && (
                    <Card>
                        <div className="p-6">
                            <div className="relative mb-6">
                                <div className="text-2xl font-semibold leading-none tracking-tight" role="heading">
                                    {t("memberDetails.sections.services.heading")}
                                </div>
                                <button
                                    onClick={() => setIsEditServicesDialogOpen(true)}
                                    className="absolute top-0 right-0 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                                    aria-label={t("memberDetails.buttons.edit.services.ariaLabel")}
                                    data-testid="edit-member-services-button"
                                >
                                    <Pencil className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
                                </button>
                            </div>

                            <div className="space-y-6">
                                {/* Driver Status */}
                                <div className="flex items-center gap-3">
                                    <Car className="h-4 w-4 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                    <Text>{t("memberDetails.sections.services.driver.label")}: {memberData.isDriver ? tCommon("yes") : tCommon("no")}</Text>
                                    {memberData.isDriver ? (
                                        <Check className="h-4 w-4 text-green-600 dark:text-green-500" aria-hidden="true"/>
                                    ) : (
                                        <X className="h-4 w-4 text-gray-400 dark:text-gray-500" aria-hidden="true"/>
                                    )}
                                </div>

                                {/* Assigned Services Section */}
                                <div
                                    className="border-t border-gray-200 dark:border-gray-700 pt-6"
                                    role="group"
                                    aria-labelledby="assigned-services-label"
                                >
                                    <div className="flex items-center gap-3 mb-2">
                                        <Briefcase className="h-4 w-4 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                        <Text className="font-medium" id="assigned-services-label">{t("memberDetails.sections.services.assignedServices.heading")}</Text>
                                    </div>
                                    <div className="pl-7">
                                        {memberServices.length > 0 ? (
                                            <div className="flex flex-wrap gap-2" role="list" aria-label={t("memberDetails.sections.services.assignedServices.heading")}>
                                                {memberServices.map(service => (
                                                    <Badge
                                                        key={service.id}
                                                        color={service.isDeleted ? 'zinc' : 'blue'}
                                                        role="listitem"
                                                        aria-label={`${service.name}${service.isDeleted ? ' (gelöscht)' : ''}`}
                                                    >
                                                        {service.name}
                                                    </Badge>
                                                ))}
                                            </div>
                                        ) : (
                                            <Text className="italic text-gray-500 dark:text-gray-400">
                                                {t("memberDetails.sections.services.noServices")}
                                            </Text>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                    )}

                    <MemberConsentSection 
                        member={memberData}
                        onUpdate={() => loadAllData()}
                    />

                    <Card>
                        <div className="p-6">
                            <div className="relative mb-6">
                                <div className="text-2xl font-semibold leading-none tracking-tight" role="heading">
                                    {t("memberDetails.sections.banking.heading")}
                                </div>
                                <button
                                    onClick={() => setIsEditBankingDetailsDialogOpen(true)}
                                    className="absolute top-0 right-0 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                                    aria-label={t("memberDetails.buttons.edit.banking.ariaLabel")}
                                    data-testid="edit-member-banking-button"
                                >
                                    <Pencil className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="flex items-center gap-3">
                                    <User className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
                                    <Text>{t("memberDetails.sections.banking.accountOwner")}:&#32;
                                        {accountOwner(memberData.bankingDetails?.accountOwner)}
                                    </Text>
                                </div>
                                <div className="flex items-center gap-3">
                                    <CreditCard className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
                                    <Text>{t("memberDetails.sections.banking.iban")}:&#32;
                                        {formatIBAN(memberData.bankingDetails?.iban)}
                                    </Text>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Banknote className="h-4 w-4 text-gray-500"/>
                                    <Text>
                                        {t("memberDetails.sections.banking.bic")}:&#32;
                                        {memberData.bankingDetails?.bic ?? tCommon("unknown-detail")}
                                    </Text>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Building className="h-4 w-4 text-gray-500"/>
                                    <Text>
                                        {t("memberDetails.sections.banking.bankName")}:&#32;
                                        {memberData.bankingDetails?.bankName ?? tCommon("unknown-detail")}
                                    </Text>
                                </div>
                                <div className="flex items-center gap-3">
                                    <FileSignature className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0"/>
                                    <Text>{t("memberDetails.sections.banking.mandateReference")}:&#32;
                                        {memberData.bankingDetails?.mandateReference}
                                    </Text>
                                </div>
                                <div className="flex items-center gap-3">
                                    <CalendarDays className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
                                    <Text>{t("memberDetails.sections.banking.mandateDate")}:&#32;
                                        {
                                            memberData.bankingDetails?.mandateDate ?? tCommon("unknown-detail")
                                        }
                                    </Text>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Clock className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
                                    <Text>{t("memberDetails.sections.banking.updatedAt")}:&#32;
                                        {
                                            memberData.bankingDetails?.updatedAt ?
                                                formatDateTime(memberData.bankingDetails?.updatedAt) :
                                                tCommon("unknown-detail")
                                        }
                                    </Text>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Donations Section */}
                    <Card>
                        <div className="p-6">
                            <div className="mb-6">
                                <div className="text-2xl font-semibold leading-none tracking-tight" role="heading">
                                    {t("memberDetails.sections.donations.heading")}
                                </div>
                            </div>

                            <div className="space-y-6">
                                {/* Annual Donations Subsection */}
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-sm font-medium">
                                            <CalendarDays className="h-4 w-4 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                            <Subheading level={3}>{t("memberDetails.sections.donations.annualDonations.heading")}</Subheading>
                                            {donations && donations.annualDonations.filter(d => !d.isActive).length > 0 && (
                                                <button
                                                    onClick={() => setIsAnnualDonationHistoryOpen(true)}
                                                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                                                    aria-label={t("memberDetails.sections.donations.buttons.showHistory.ariaLabel")}
                                                    data-testid="show-annual-donation-history-button"
                                                >
                                                    <Info className="h-4 w-4 text-gray-400 dark:text-gray-500"/>
                                                </button>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => setIsAddAnnualDonationDialogOpen(true)}
                                            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                                            aria-label={t("memberDetails.sections.donations.buttons.addAnnual.ariaLabel")}
                                            data-testid="add-annual-donation-button"
                                        >
                                            <Plus className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
                                        </button>
                                    </div>

                                    {donations?.nextBillingDate && (
                                        <Text className="text-sm text-gray-500 dark:text-gray-400 pl-6">
                                            {t("memberDetails.sections.donations.annualDonations.nextBilling")}: {formatDate(donations.nextBillingDate)}
                                        </Text>
                                    )}

                                    {donations && donations.annualDonations.length > 0 ? (
                                        <div className="pl-6 space-y-2">
                                            {donations.annualDonations.map((donation) => (
                                                <div key={donation.id} className="flex items-center gap-3">
                                                    {donation.isActive ? (
                                                        <Badge color="green">{t("memberDetails.sections.donations.annualDonations.active")}</Badge>
                                                    ) : (
                                                        <Badge color="zinc">{t("memberDetails.sections.donations.annualDonations.ended")}</Badge>
                                                    )}
                                                    <Text>
                                                        {donation.amount.toFixed(2)} € / {t("memberDetails.sections.donations.annualDonations.year")}
                                                    </Text>
                                                    <Text className="text-gray-500 dark:text-gray-400">
                                                        ({t("memberDetails.sections.donations.annualDonations.validFrom")} {formatDate(donation.validFrom)}
                                                        {donation.validUntil && ` - ${formatDate(donation.validUntil)}`})
                                                    </Text>
                                                    {donation.isBilled && (
                                                        <div className="p-1.5" title={t("memberDetails.sections.donations.annualDonations.billed")}>
                                                            <Lock className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500"/>
                                                        </div>
                                                    )}
                                                    {donation.isActive && (
                                                        <div className={`flex items-center gap-1 ${!donation.isBilled ? 'ml-auto' : ''}`}>
                                                            <button
                                                                onClick={() => handleEditAnnualDonation(donation)}
                                                                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                                                                aria-label={t("memberDetails.sections.donations.buttons.editAnnual.ariaLabel")}
                                                                data-testid={`edit-annual-donation-${donation.id}`}
                                                            >
                                                                <Pencil className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400"/>
                                                            </button>
                                                            <button
                                                                onClick={() => handleEndAnnualDonation(donation)}
                                                                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                                                                aria-label={t("memberDetails.sections.donations.buttons.endAnnual.ariaLabel")}
                                                                data-testid={`end-annual-donation-${donation.id}`}
                                                            >
                                                                <StopCircle className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400"/>
                                                            </button>
                                                        </div>
                                                    )}
                                                    {!donation.isActive && !donation.isBilled && (
                                                        <div className="ml-auto" />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <Text className="italic pl-6 text-gray-500 dark:text-gray-400">
                                            {t("memberDetails.sections.donations.annualDonations.empty")}
                                        </Text>
                                    )}
                                </div>

                                <Divider soft />

                                {/* One-Time Donations Subsection */}
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-sm font-medium">
                                            <Gift className="h-4 w-4 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                            <Subheading level={3}>{t("memberDetails.sections.donations.oneTimeDonations.heading")}</Subheading>
                                        </div>
                                        <button
                                            onClick={() => setIsAddOneTimeDonationDialogOpen(true)}
                                            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                                            aria-label={t("memberDetails.sections.donations.buttons.addOneTime.ariaLabel")}
                                            data-testid="add-one-time-donation-button"
                                        >
                                            <Plus className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
                                        </button>
                                    </div>

                                    {donations && donations.oneTimeDonations.length > 0 ? (
                                        <div className="pl-6 space-y-2">
                                            {donations.oneTimeDonations.map((donation) => (
                                                <div key={donation.id} className="flex items-center gap-3">
                                                    <Text>{donation.amount.toFixed(2)} €</Text>
                                                    <Text className="text-gray-500 dark:text-gray-400">
                                                        ({formatDate(donation.donationDate)})
                                                    </Text>
                                                    {donation.isBilled ? (
                                                        <div className="ml-auto p-1.5" title={t("memberDetails.sections.donations.oneTimeDonations.billed")}>
                                                            <Lock className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500"/>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-1 ml-auto">
                                                            <button
                                                                onClick={() => handleEditOneTimeDonation(donation)}
                                                                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                                                                aria-label={t("memberDetails.sections.donations.buttons.editOneTime.ariaLabel")}
                                                                data-testid={`edit-one-time-donation-${donation.id}`}
                                                            >
                                                                <Pencil className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400"/>
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteOneTimeDonation(donation)}
                                                                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                                                                aria-label={t("memberDetails.sections.donations.buttons.deleteOneTime.ariaLabel")}
                                                                data-testid={`delete-one-time-donation-${donation.id}`}
                                                            >
                                                                <Trash2 className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400"/>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <Text className="italic pl-6 text-gray-500 dark:text-gray-400">
                                            {t("memberDetails.sections.donations.oneTimeDonations.empty")}
                                        </Text>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Card>

                    <Card>
                        <div className="p-6">
                            <div className="relative mb-6">
                                <div className="text-2xl font-semibold leading-none tracking-tight" role="heading">
                                    {t("memberDetails.sections.additionalInfo.heading")}
                                </div>
                                <button
                                    onClick={() => setIsEditCommentsDialogOpen(true)}
                                    className="absolute top-0 right-0 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                                    aria-label={t("memberDetails.buttons.edit.additionalInfo.ariaLabel")}
                                    data-testid="edit-member-comments-button"
                                >
                                    <Pencil className="h-4 w-4 text-gray-500 dark:text-gray-400"/>
                                </button>
                            </div>

                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 text-sm font-medium">
                                        <Car className="h-4 w-4"/>
                                        <Subheading level={3}>{t("memberDetails.sections.additionalInfo.mobilityOption")}</Subheading>
                                    </div>
                                    <Text className="pl-6">
                                        {memberData.fastestMobilityOption?.name ?? t("memberDetails.sections.additionalInfo.mobilityOptionNotSet")}
                                    </Text>
                                </div>

                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 text-sm font-medium ">
                                        <MessageSquare className="h-4 w-4"/>
                                        <Subheading level={3}>{t("memberDetails.sections.comments.general")}</Subheading>
                                    </div>
                                    {memberData.generalComments ? (
                                        <Text className="pl-6 whitespace-pre-wrap">
                                            {memberData.generalComments}
                                        </Text>
                                    ) : (
                                        <Text className="italic pl-6">
                                            {t("memberDetails.sections.comments.noComments")}
                                        </Text>
                                    )}
                                </div>

                                {memberData.memberStatus === 'ACTIVE_MEMBER' && (
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2 text-sm font-medium">
                                            <Clock className="h-4 w-4 flex-shrink-0"/>
                                            <Subheading level={3}>{t("memberDetails.sections.comments.availability")}</Subheading>
                                        </div>
                                        {memberData.availabilityComments ? (
                                            <Text className="pl-6 whitespace-pre-wrap">
                                                {memberData.availabilityComments}
                                            </Text>
                                        ) : (
                                            <Text className="italic pl-6">
                                                {t("memberDetails.sections.comments.noComments")}
                                            </Text>
                                        )}
                                    </div>
                                )}

                                {memberData.memberStatus === 'ACTIVE_MEMBER' && memberData.isDriver && (
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2 text-sm font-medium">
                                            <Zap className="h-4 w-4 flex-shrink-0"/>
                                            <Subheading level={3}>{t("memberDetails.sections.comments.currentNotes")}</Subheading>
                                        </div>
                                        {memberData.currentNotes ? (
                                            <Text className="pl-6 whitespace-pre-wrap">
                                                {memberData.currentNotes}
                                            </Text>
                                        ) : (
                                            <Text className="italic pl-6">
                                                {t("memberDetails.sections.comments.noComments")}
                                            </Text>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>

                    {memberId && (
                        <MemberAuditLogSection
                            memberId={memberId}
                            refreshTrigger={auditLogRefreshTrigger}
                        />
                    )}

                </div>

                <Divider className="my-10" soft/>

                <section className="flex justify-end gap-4">
                    <Button aria-label={tCommon("buttons.back.ariaLabel")} href={returnRoute}
                            role="navigation">{tCommon("buttons.back.text")}</Button>
                </section>


                {
                    memberData && (
                        <>
                            <MemberEditNamesDialog
                                isOpen={isEditNamesDialogOpen}
                                onSave={(member) => {
                                    setMemberData(member);
                                    triggerAuditLogRefresh();
                                }}
                                onClose={() => setIsEditNamesDialogOpen(false)}
                                memberData={memberData}
                                salutations={memberFormOptions?.salutations}
                            />

                            <MemberEditCommentsDialog
                                isOpen={isEditCommentsDialogOpen}
                                onSave={(member) => {
                                    setMemberData(member);
                                    triggerAuditLogRefresh();
                                }}
                                onClose={() => setIsEditCommentsDialogOpen(false)}
                                memberData={memberData}
                            />

                            <MemberEditContactDetailsDialog
                                isOpen={isEditContactDetailsDialogOpen}
                                onSave={(member) => {
                                    setMemberData(member);
                                    triggerAuditLogRefresh();
                                }}
                                onClose={() => setIsEditContactDetailsDialogOpen(false)}
                                memberData={memberData}
                            />

                            <MemberEditMembershipDialog
                                isOpen={isEditMembershipDialogOpen}
                                onSave={async (updatedMember) => {
                                    setMemberData(updatedMember);
                                    triggerAuditLogRefresh();
                                    // Wait a bit before reloading to ensure backend has processed the changes
                                    setTimeout(() => {
                                        loadAllData(); // Reload to get updated fee assignment
                                    }, 500);
                                }}
                                onClose={() => setIsEditMembershipDialogOpen(false)}
                                memberData={memberData}
                                availableStatus={memberFormOptions?.supportedStatus || []}
                                membershipFeeTypes={membershipFeeTypes}
                                currentFeeTypeId={currentFeeAssignment?.feeTypeId}
                            />

                            <MemberEditBankingDetailsDialog
                                isOpen={isEditBankingDetailsDialogOpen}
                                onSave={(member) => {
                                    setMemberData(member);
                                    triggerAuditLogRefresh();
                                }}
                                onClose={() => setIsEditBankingDetailsDialogOpen(false)}
                                onDelete={() => {
                                    loadAllData();
                                    triggerAuditLogRefresh();
                                }}
                                memberData={memberData}
                            />

                            <MemberEditServicesDialog
                                isOpen={isEditServicesDialogOpen}
                                onSave={() => {
                                    loadAllData();
                                    triggerAuditLogRefresh();
                                }}
                                onClose={() => setIsEditServicesDialogOpen(false)}
                                memberData={memberData}
                                memberServices={memberServices}
                            />

                            {/* Donation Dialogs */}
                            <OneTimeDonationDialog
                                isOpen={isAddOneTimeDonationDialogOpen}
                                mode="add"
                                memberId={memberId!}
                                onSave={handleAddOneTimeDonationSuccess}
                                onClose={() => setIsAddOneTimeDonationDialogOpen(false)}
                            />

                            <OneTimeDonationDialog
                                isOpen={isEditOneTimeDonationDialogOpen}
                                mode="edit"
                                memberId={memberId!}
                                donation={editingOneTimeDonation ?? undefined}
                                onSave={handleEditOneTimeDonationSuccess}
                                onClose={() => {
                                    setIsEditOneTimeDonationDialogOpen(false);
                                    setEditingOneTimeDonation(null);
                                }}
                            />

                            <AnnualDonationDialog
                                isOpen={isAddAnnualDonationDialogOpen}
                                mode="add"
                                memberId={memberId!}
                                onSave={handleAddAnnualDonationSuccess}
                                onClose={() => setIsAddAnnualDonationDialogOpen(false)}
                            />

                            <AnnualDonationDialog
                                isOpen={isEditAnnualDonationDialogOpen}
                                mode="edit"
                                memberId={memberId!}
                                donation={editingAnnualDonation ?? undefined}
                                onSave={handleEditAnnualDonationSuccess}
                                onClose={() => {
                                    setIsEditAnnualDonationDialogOpen(false);
                                    setEditingAnnualDonation(null);
                                }}
                            />

                            {/* Confirmation Dialogs */}
                            <ConfirmationDialog
                                isOpen={isDeleteOneTimeConfirmOpen}
                                onCancel={() => {
                                    setIsDeleteOneTimeConfirmOpen(false);
                                    setDeletingOneTimeDonation(null);
                                }}
                                onConfirm={confirmDeleteOneTimeDonation}
                                config={{
                                    title: "memberDetails.sections.donations.confirmDeleteOneTime.title",
                                    description: "memberDetails.sections.donations.confirmDeleteOneTime.message",
                                    confirmButton: {
                                        text: "buttons.delete.text",
                                        ariaLabel: "buttons.delete.ariaLabel",
                                        color: "red"
                                    },
                                    cancelButton: {
                                        text: "buttons.cancel.text",
                                        ariaLabel: "buttons.cancel.ariaLabel"
                                    }
                                }}
                                namespace="members"
                            />

                            <ConfirmationDialog
                                isOpen={isEndAnnualConfirmOpen}
                                onCancel={() => {
                                    setIsEndAnnualConfirmOpen(false);
                                    setEndingAnnualDonation(null);
                                }}
                                onConfirm={confirmEndAnnualDonation}
                                config={{
                                    title: "memberDetails.sections.donations.confirmEndAnnual.title",
                                    description: "memberDetails.sections.donations.confirmEndAnnual.message",
                                    confirmButton: {
                                        text: "buttons.confirm.text",
                                        ariaLabel: "buttons.confirm.ariaLabel",
                                        color: "amber"
                                    },
                                    cancelButton: {
                                        text: "buttons.cancel.text",
                                        ariaLabel: "buttons.cancel.ariaLabel"
                                    }
                                }}
                                namespace="members"
                            />

                            {/* Annual Donation History Dialog */}
                            <Dialog open={isAnnualDonationHistoryOpen} onClose={() => setIsAnnualDonationHistoryOpen(false)}>
                                <DialogTitle>{t("memberDetails.sections.donations.history.title")}</DialogTitle>
                                <DialogBody>
                                    <div className="space-y-3">
                                        {donations?.annualDonations
                                            .filter(d => !d.isActive)
                                            .slice(0, 5)
                                            .map((donation) => (
                                                <div key={donation.id} className="flex items-center gap-3 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0">
                                                    <Text className="font-medium">
                                                        {donation.amount.toFixed(2)} € / {t("memberDetails.sections.donations.annualDonations.year")}
                                                    </Text>
                                                    <Text className="text-gray-500 dark:text-gray-400">
                                                        {formatDate(donation.validFrom)} - {donation.validUntil ? formatDate(donation.validUntil) : ''}
                                                    </Text>
                                                    {donation.isBilled && (
                                                        <span title={t("memberDetails.sections.donations.annualDonations.billed")}>
                                                            <Lock className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500"/>
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        {donations?.annualDonations.filter(d => !d.isActive).length === 0 && (
                                            <Text className="text-gray-500 dark:text-gray-400 italic">
                                                {t("memberDetails.sections.donations.history.empty")}
                                            </Text>
                                        )}
                                    </div>
                                    <div className="mt-4 flex justify-end">
                                        <Button plain onClick={() => setIsAnnualDonationHistoryOpen(false)}>
                                            {tCommon("buttons.close.text")}
                                        </Button>
                                    </div>
                                </DialogBody>
                            </Dialog>
                        </>
                    )
                }

            </>
        )
    }
}
